import { MazeState, PathSegment, WallSegment } from '../types/maze-types';
import { MAZE_COLORS, GAME_COLORS, getMazeBackground, getZoneColors } from '@/contexts/games/constants/colors';
import { MAZE_EMOJIS } from '../config/mazeEmojis';

/**
 * Синхронный рендерер лабиринта
 */
export class MazeRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Cannot get 2D context from canvas');
    }
    this.ctx = ctx;
  }

  /**
   * Полная отрисовка лабиринта
   */
  render(state: MazeState, includePath: boolean = true): void {
    this.clearCanvas(state);
    this.drawWalls(state);
    if (includePath) {
      this.drawPath(state);
    }
    // Draw zone emojis last so they render on top of walls
    this.drawZones(state);
  }

  /**
   * Создание PNG для скачивания
   */
  createDownloadImage(state: MazeState): string {
    // Создаем временный канвас
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = this.canvas.width;
    tempCanvas.height = this.canvas.height;
    
    const tempRenderer = new MazeRenderer(tempCanvas);
    tempRenderer.render(state, false); // Без пути пользователя
    
    return tempCanvas.toDataURL('image/png');
  }

  private clearCanvas(state: MazeState): void {
    this.ctx.fillStyle = getMazeBackground(state.isDarkMode ?? false);
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  private drawZones(state: MazeState): void {
    if (state.entranceZone) {
      this.drawZone(state.entranceZone, state, GAME_COLORS.success);
    }
    if (state.exitZone) {
      this.drawZone(state.exitZone, state, GAME_COLORS.destructive);
    }
  }

  private getOffset(state: MazeState): { offsetX: number; offsetY: number } {
    const mazeWidth = state.cols * state.cellSize;
    const mazeHeight = state.rows * state.cellSize;
    const offsetX = (state.canvasSize.width - mazeWidth) / 2;
    const offsetY = (state.canvasSize.height - mazeHeight) / 2;
    return { offsetX: Math.max(0, offsetX), offsetY: Math.max(0, offsetY) };
  }

  private drawZone(zone: { topLeft: { x: number; y: number }; size: number; image?: string }, state: MazeState, _defaultColor: string): void {
    const { offsetX, offsetY } = this.getOffset(state);
    const x = zone.topLeft.x * state.cellSize + offsetX;
    const y = zone.topLeft.y * state.cellSize + offsetY;
    const width = zone.size * state.cellSize;
    const height = zone.size * state.cellSize;

    if (zone.image) {
      this.drawEmojiInZone(zone.image, x, y, width, height, state.isDarkMode);
    }
  }

  private drawEmojiInZone(emoji: string, x: number, y: number, width: number, height: number, isDarkMode?: boolean): void {
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    const radius = Math.min(width, height) * 0.25;

    // Disc behind the emoji so walls don't show through, with the fleet's marker
    // rim (blue = entry, amber = exit; brighter hues on the dark theme) — the
    // plain white disc on a dark board was the one thing a player could not find.
    const zc = getZoneColors(!!isDarkMode);
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    this.ctx.fillStyle = isDarkMode ? MAZE_COLORS.badgeFillDark : MAZE_COLORS.badgeFillLight;
    this.ctx.fill();
    this.ctx.lineWidth = Math.max(2, radius * 0.18);
    this.ctx.strokeStyle = emoji === MAZE_EMOJIS.entrance ? zc.entryRing : zc.exitRing;
    this.ctx.stroke();
    this.ctx.restore();

    // Draw emoji
    const fontSize = Math.min(width, height) * 0.45;
    this.ctx.font = `${fontSize}px Arial`;
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(emoji, centerX, centerY);
  }

  private drawWalls(state: MazeState): void {
    const { offsetX, offsetY } = this.getOffset(state);
    this.ctx.strokeStyle = state.isDarkMode ? MAZE_COLORS.wallDark : MAZE_COLORS.wallLight;
    this.ctx.lineWidth = 4;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    // No shadow — cleaner look

    // If we have wall segments (curved walls), render those
    if (state.wallSegments && state.wallSegments.length > 0) {
      this.drawWallSegments(state.wallSegments, offsetX, offsetY);
    } else {
      // Legacy rendering for straight walls
      this.drawStraightWalls(state, offsetX, offsetY);
    }

  }

  private drawWallSegments(wallSegments: WallSegment[], offsetX: number, offsetY: number): void {
    this.ctx.beginPath();

    for (const wall of wallSegments) {
      this.ctx.moveTo(wall.start.x + offsetX, wall.start.y + offsetY);

      if (wall.type === 'line') {
        this.ctx.lineTo(wall.end.x + offsetX, wall.end.y + offsetY);
      } else if (wall.type === 'bezier' && wall.control1) {
        this.ctx.quadraticCurveTo(
          wall.control1.x + offsetX,
          wall.control1.y + offsetY,
          wall.end.x + offsetX,
          wall.end.y + offsetY
        );
      } else if (wall.type === 'arc' && wall.control1 && wall.radius) {
        this.ctx.arcTo(
          wall.control1.x + offsetX,
          wall.control1.y + offsetY,
          wall.end.x + offsetX,
          wall.end.y + offsetY,
          wall.radius
        );
      }
    }

    this.ctx.stroke();
  }

  private drawStraightWalls(state: MazeState, offsetX: number, offsetY: number): void {
    this.ctx.beginPath();

    for (let row = 0; row < state.rows; row++) {
      for (let col = 0; col < state.cols; col++) {
        const cell = state.maze[row][col];
        const x = col * state.cellSize + offsetX;
        const y = row * state.cellSize + offsetY;

        if (cell.walls.top) {
          this.ctx.moveTo(x, y);
          this.ctx.lineTo(x + state.cellSize, y);
        }
        if (cell.walls.right) {
          this.ctx.moveTo(x + state.cellSize, y);
          this.ctx.lineTo(x + state.cellSize, y + state.cellSize);
        }
        if (cell.walls.bottom) {
          this.ctx.moveTo(x, y + state.cellSize);
          this.ctx.lineTo(x + state.cellSize, y + state.cellSize);
        }
        if (cell.walls.left) {
          this.ctx.moveTo(x, y);
          this.ctx.lineTo(x, y + state.cellSize);
        }
      }
    }

    this.ctx.stroke();
  }

  private drawPath(state: MazeState): void {
    const { offsetX, offsetY } = this.getOffset(state);
    state.pathSegments.forEach((segment: PathSegment) => {
      if (segment.points.length < 2) return;

      this.ctx.strokeStyle = segment.color;
      this.ctx.lineWidth = 3;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.beginPath();

      this.ctx.moveTo(segment.points[0].x + offsetX, segment.points[0].y + offsetY);
      for (let i = 1; i < segment.points.length; i++) {
        this.ctx.lineTo(segment.points[i].x + offsetX, segment.points[i].y + offsetY);
      }
      this.ctx.stroke();
    });
  }
}
